import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';
import { useApi, useMutation } from '../../hooks/useApi';
import logger from '../../utils/logger';
import {
  FileDown,
  ChevronsLeft,
  ChevronLeft,
  ChevronRight,
  ChevronsRight,
  AlertTriangle
} from 'lucide-react';

function RaportStokUlet() {
  const [pragu, setPragu] = useState(5);
  const [furnizuesId, setFurnizuesId] = useState('');
  const [error, setError] = useState('');
  const [faqjaAktuale, setFaqjaAktuale] = useState(1);
  const perFaqe = 5;

  const { data: produktet, loading, execute: loadData } = useApi(
    () => apiService.get('/raport-stok-ulet', { params: { pragu, furnizues_id: furnizuesId || undefined } }),
    [pragu, furnizuesId],
    { immediate: true, showNotifications: false }
  );

  const { data: furnizuesitData } = useApi(
    () => apiService.get('/furnizuesit'),
    [],
    { immediate: true, showNotifications: false }
  );

  const furnizuesit = Array.isArray(furnizuesitData) ? furnizuesitData : (furnizuesitData?.data || []);

  useEffect(() => {
    if (produktet) {
      setFaqjaAktuale(1);
    }
  }, [produktet]);

  const handleExportPdf = async () => {
    try {
      const response = await apiService.get('/raport-stok-ulet/pdf', {
        params: { pragu, furnizues_id: furnizuesId || undefined },
        responseType: 'blob'
      });
      const file = new Blob([response], { type: 'application/pdf' });
      const fileURL = URL.createObjectURL(file);
      window.open(fileURL);
    } catch (err) {
      logger.error('PDF export error:', err);
      setError('Dështoi shkarkimi i PDF-it.');
    }
  };

  const renderTabela = (lista, lokacioni) => {
    const offset = (faqjaAktuale - 1) * perFaqe;
    const faqja = lista.slice(offset, offset + perFaqe);
    const faqetTotale = Math.ceil(lista.length / perFaqe);

    return (
      <div key={lokacioni} className="space-y-6">
        <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300">Lokacioni: {lokacioni}</h3>

        {/* Paginimi */}
        <div className="flex justify-between items-center text-sm text-gray-600 dark:text-gray-400">
          <span>Faqe {faqjaAktuale} nga {faqetTotale}</span>
          <div className="flex gap-1">
            <button onClick={() => setFaqjaAktuale(1)} disabled={faqjaAktuale === 1} className="btn-premium-secondary disabled:opacity-50 disabled:cursor-not-allowed"><ChevronsLeft size={16} /></button>
            <button onClick={() => setFaqjaAktuale(faqjaAktuale - 1)} disabled={faqjaAktuale === 1} className="btn-premium-secondary disabled:opacity-50 disabled:cursor-not-allowed"><ChevronLeft size={16} /></button>
            <button onClick={() => setFaqjaAktuale(faqjaAktuale + 1)} disabled={faqjaAktuale === faqetTotale} className="btn-premium-secondary disabled:opacity-50 disabled:cursor-not-allowed"><ChevronRight size={16} /></button>
            <button onClick={() => setFaqjaAktuale(faqetTotale)} disabled={faqjaAktuale === faqetTotale} className="btn-premium-secondary disabled:opacity-50 disabled:cursor-not-allowed"><ChevronsRight size={16} /></button>
          </div>
        </div>

        {/* Tabela desktop */}
        <div className="hidden md:block">
          <div className="card-premium overflow-hidden p-0">
            <table className="table-premium">
              <thead>
                <tr>
                  <th>Produkti</th>
                  <th>Madhësia</th>
                  <th>Ngjyra</th>
                  <th className="text-right">Stoku</th>
                </tr>
              </thead>
              <tbody>
                {faqja.map((p, i) => (
                  <tr key={i}>
                    <td className="font-medium">{p.produkti}</td>
                    <td>{p.madhesia}</td>
                    <td>{p.ngjyrat}</td>
                    <td className={`text-right font-semibold ${p.stoku <= 0 ? 'text-red-600 dark:text-red-400' : 'text-yellow-600 dark:text-yellow-400'}`}>
                      {p.stoku}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Kartela për mobile */}
        <div className="md:hidden space-y-4">
          {faqja.map((p, i) => (
            <div key={i} className="card-premium p-4 space-y-1">
              <div><strong className="text-gray-700 dark:text-gray-300">Produkti:</strong> {p.produkti}</div>
              <div><strong className="text-gray-700 dark:text-gray-300">Madhësia:</strong> {p.madhesia}</div>
              <div><strong className="text-gray-700 dark:text-gray-300">Ngjyra:</strong> {p.ngjyrat}</div>
              <div className={`font-semibold ${p.stoku <= 0 ? 'text-red-600 dark:text-red-400' : 'text-yellow-600 dark:text-yellow-400'}`}>
                <strong>Stoku:</strong> {p.stoku}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2">
        <AlertTriangle className="text-orange-500 dark:text-orange-400" size={22} /> Produktet me Stok të Ulët
      </h2>

      <div className="card-premium p-4 flex flex-wrap items-center gap-3">
        <label className="form-label mb-0">Pragu i stokut:</label>
        <input
          type="number"
          value={pragu}
          onChange={(e) => setPragu(Number(e.target.value))}
          className="input-premium w-24"
          min={0}
        />
        <label className="form-label mb-0">Furnizuesi:</label>
        <select
          value={furnizuesId}
          onChange={(e) => setFurnizuesId(e.target.value)}
          className="input-premium min-w-[200px]"
        >
          <option value="">Të gjithë</option>
          {furnizuesit.map((f) => (
            <option key={f.id} value={f.id}>{f.emri}</option>
          ))}
        </select>
      </div>

      {error && <p className="p-3 rounded-lg bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300 font-medium">{error}</p>}

      {loading && <p className="text-gray-500 dark:text-gray-400">Duke ngarkuar...</p>}

      {produktet && Object.keys(produktet).length > 0 ? (
        <div className="space-y-10">
          <button
            onClick={handleExportPdf}
            className="btn-premium flex items-center gap-2"
          >
            <FileDown size={18} /> Shkarko PDF
          </button>

          {Object.entries(produktet).map(([lokacioni, lista]) =>
            renderTabela(lista, lokacioni)
          )}
        </div>
      ) : (
        <p className="text-gray-500 dark:text-gray-400 mt-4">Nuk ka produkte me stok më të ulët se {pragu}.</p>
      )}
    </div>
  );
}

export default RaportStokUlet;
